import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Package,
  Truck,
  ShoppingCart,
  Box,
  TrendingUp,
  Tag,
  Check,
  Loader2,
  Bell,
  RefreshCw,
  Settings,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';

interface N11Integration {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  descriptionEn: string;
  tooltip: string;
  tooltipEn: string;
  icon: typeof Package;
  enabled: boolean;
  status: 'active' | 'inactive' | 'error';
}

interface N11SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeName: string;
  connectionId: string;
  onSave: (settings: N11Settings) => void;
}

export interface N11Settings {
  syncFrequency: number;
  notifications: boolean;
  integrations: Record<string, boolean>;
}

const defaultIntegrations: N11Integration[] = [
  {
    id: 'listings',
    name: 'Urun Listeleme',
    nameEn: 'Product Listings',
    description: 'Urunlerinizi, stok ve fiyat bilgilerini goruntuluyin',
    descriptionEn: 'View your products, stock and price information',
    tooltip: 'Tum urunlerinizi, stok durumlarini, fiyatlarini ve satis durumlarini takip edin.',
    tooltipEn: 'Track all your products, stock levels, prices and sale status.',
    icon: Box,
    enabled: true,
    status: 'active',
  },
  {
    id: 'orders',
    name: 'Siparis Entegrasyonu',
    nameEn: 'Order Integration',
    description: 'Siparisleri otomatik olarak cek ve analiz et',
    descriptionEn: 'Automatically fetch and analyze orders',
    tooltip: 'Tum siparis statuslerini takip edin. Siparis trendlerini ve en cok satan urunleri analiz edin.',
    tooltipEn: 'Track all order statuses. Analyze order trends and best-selling products.',
    icon: ShoppingCart,
    enabled: true,
    status: 'active',
  },
  {
    id: 'categories',
    name: 'Kategori Bilgileri',
    nameEn: 'Category Information',
    description: 'N11 kategori agacini ve ozelliklerini goruntuluyin',
    descriptionEn: 'View N11 category tree and attributes',
    tooltip: 'Tum N11 kategorilerini ve alt kategorileri gorun. Urun kategori eslestirmesi yapin.',
    tooltipEn: 'See all N11 categories and subcategories. Match product categories.',
    icon: Tag,
    enabled: true,
    status: 'active',
  },
  {
    id: 'shipping',
    name: 'Kargo Takibi',
    nameEn: 'Shipping Tracking',
    description: 'Kargo durumlarini ve teslimat surelerini takip edin',
    descriptionEn: 'Track shipping status and delivery times',
    tooltip: 'Gonderilen paketlerin durumlarini ve kargo firmasi bilgilerini izleyin.',
    tooltipEn: 'Monitor package status and carrier information.',
    icon: Truck,
    enabled: true,
    status: 'active',
  },
  {
    id: 'analytics',
    name: 'Analitik & Raporlar',
    nameEn: 'Analytics & Reports',
    description: 'Satış analizleri ve performans raporları',
    descriptionEn: 'Sales analytics and performance reports',
    tooltip: 'Satis performansinizi analiz edin, trendleri takip edin ve raporlar olusturun.',
    tooltipEn: 'Analyze your sales performance, track trends and generate reports.',
    icon: TrendingUp,
    enabled: true,
    status: 'active',
  },
];

export function N11SettingsModal({
  isOpen,
  onClose,
  storeName,
  connectionId,
  onSave,
}: N11SettingsModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const { user } = useAuth();

  const [integrations, setIntegrations] = useState<N11Integration[]>(defaultIntegrations);
  const [syncFrequency, setSyncFrequency] = useState(30);
  const [notifications, setNotifications] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load existing settings
  useEffect(() => {
    const loadSettings = async () => {
      if (!isOpen || !user?.uid || !connectionId) return;

      setIsLoading(true);
      try {
        const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
        const response = await fetch(
          `${FUNCTIONS_URL}/getN11Settings?userId=${user.uid}&connectionId=${connectionId}`
        );
        const result = await response.json();

        if (result.settings) {
          if (result.settings.syncFrequency) {
            setSyncFrequency(result.settings.syncFrequency);
          }
          if (typeof result.settings.notifications === 'boolean') {
            setNotifications(result.settings.notifications);
          }
          if (result.settings.integrations) {
            setIntegrations(prev =>
              prev.map(int => ({
                ...int,
                enabled: result.settings.integrations[int.id] ?? int.enabled,
              }))
            );
          }
        }
      } catch (error) {
        console.error('Error loading N11 settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [isOpen, user?.uid, connectionId]);

  const handleToggleIntegration = (id: string) => {
    setIntegrations(prev =>
      prev.map(int => (int.id === id ? { ...int, enabled: !int.enabled } : int))
    );
  };

  const handleSave = async () => {
    if (!user?.uid || !connectionId) return;

    setIsSaving(true);
    try {
      const settings: N11Settings = {
        syncFrequency,
        notifications,
        integrations: integrations.reduce((acc, int) => {
          acc[int.id] = int.enabled;
          return acc;
        }, {} as Record<string, boolean>),
      };

      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

      const response = await fetch(`${FUNCTIONS_URL}/saveN11Settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          connectionId,
          settings,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSaveSuccess(true);
        onSave(settings);
        setTimeout(() => {
          setSaveSuccess(false);
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving N11 settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border-default flex flex-col">
        {/* Header */}
        <div
          className="relative p-6 flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg, #00B14F 0%, #00963F 100%)',
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Settings size={24} className="text-white" />
            </div>
            <div className="text-white">
              <h2 className="text-xl font-bold">N11 {isEnglish ? 'Settings' : 'Ayarlari'}</h2>
              <p className="text-white/80 text-sm">{storeName}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="text-[#00B14F] animate-spin" />
            </div>
          ) : saveSuccess ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                <CheckCircle2 size={32} className="text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary">
                {isEnglish ? 'Settings Saved!' : 'Ayarlar Kaydedildi!'}
              </h3>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Sync Settings */}
              <div className="bg-bg-elevated rounded-xl p-4 border border-border-default">
                <div className="flex items-center gap-3 mb-4">
                  <RefreshCw size={20} className="text-[#00B14F]" />
                  <h3 className="font-semibold text-text-primary">
                    {isEnglish ? 'Sync Settings' : 'Senkronizasyon Ayarlari'}
                  </h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">
                      {isEnglish ? 'Sync Frequency' : 'Senkronizasyon Sikligi'}
                    </label>
                    <select
                      value={syncFrequency}
                      onChange={(e) => setSyncFrequency(Number(e.target.value))}
                      className="w-full px-3 py-2 rounded-lg bg-bg-surface border border-border-default text-text-primary focus:outline-none focus:border-[#00B14F]"
                    >
                      <option value={5}>{isEnglish ? 'Every 5 minutes' : 'Her 5 dakika'}</option>
                      <option value={15}>{isEnglish ? 'Every 15 minutes' : 'Her 15 dakika'}</option>
                      <option value={30}>{isEnglish ? 'Every 30 minutes' : 'Her 30 dakika'}</option>
                      <option value={60}>{isEnglish ? 'Every hour' : 'Her saat'}</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Bell size={18} className="text-text-tertiary" />
                      <span className="text-sm text-text-primary">
                        {isEnglish ? 'Enable Notifications' : 'Bildirimleri Etkinlestir'}
                      </span>
                    </div>
                    <button
                      onClick={() => setNotifications(!notifications)}
                      className={cn(
                        'w-12 h-6 rounded-full transition-colors relative',
                        notifications ? 'bg-[#00B14F]' : 'bg-bg-surface border border-border-default'
                      )}
                    >
                      <div
                        className={cn(
                          'w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-all',
                          notifications ? 'left-6' : 'left-0.5'
                        )}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Integrations */}
              <div>
                <h3 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <Package size={20} className="text-[#00B14F]" />
                  {isEnglish ? 'Active Integrations' : 'Aktif Entegrasyonlar'}
                </h3>

                <div className="space-y-3">
                  {integrations.map((integration) => {
                    const Icon = integration.icon;
                    return (
                      <div
                        key={integration.id}
                        className={cn(
                          'p-4 rounded-xl border transition-all cursor-pointer',
                          integration.enabled
                            ? 'bg-[#00B14F]/5 border-[#00B14F]/30'
                            : 'bg-bg-elevated border-border-default hover:border-[#00B14F]/30'
                        )}
                        onClick={() => handleToggleIntegration(integration.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={cn(
                                'w-10 h-10 rounded-lg flex items-center justify-center',
                                integration.enabled ? 'bg-[#00B14F]/10' : 'bg-bg-surface'
                              )}
                            >
                              <Icon
                                size={20}
                                className={integration.enabled ? 'text-[#00B14F]' : 'text-text-tertiary'}
                              />
                            </div>
                            <div>
                              <h4 className="font-medium text-text-primary">
                                {isEnglish ? integration.nameEn : integration.name}
                              </h4>
                              <p className="text-xs text-text-secondary">
                                {isEnglish ? integration.descriptionEn : integration.description}
                              </p>
                            </div>
                          </div>

                          <div
                            className={cn(
                              'w-6 h-6 rounded-full flex items-center justify-center transition-colors',
                              integration.enabled
                                ? 'bg-[#00B14F] text-white'
                                : 'bg-bg-surface border border-border-default'
                            )}
                          >
                            {integration.enabled && <Check size={14} />}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Info Banner */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-[#00B14F]/5 border border-[#00B14F]/20">
                <Info size={20} className="text-[#00B14F] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-text-secondary">
                  {isEnglish
                    ? 'N11 REST API provides real-time data access. Changes may take a few minutes to reflect.'
                    : 'N11 REST API gercek zamanli veri erisimi saglar. Degisikliklerin yansimasi birkaç dakika sürebilir.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!isLoading && !saveSuccess && (
          <div className="p-4 border-t border-border-default flex-shrink-0">
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 rounded-xl bg-bg-elevated border border-border-default text-text-secondary hover:text-text-primary transition-colors font-medium"
              >
                {isEnglish ? 'Cancel' : 'Iptal'}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={cn(
                  'flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2',
                  'bg-[#00B14F] text-white hover:bg-[#00963F]',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isSaving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    {isEnglish ? 'Saving...' : 'Kaydediliyor...'}
                  </>
                ) : (
                  isEnglish ? 'Save Settings' : 'Ayarlari Kaydet'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
